import EventNoteIcon from '@mui/icons-material/EventNote';
import { styled } from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import { gridClasses } from '@mui/x-data-grid';
import {
  ActivityLogDTO,
  ClientContactDTO,
  ClientContactShareMultipleDTO,
  ClientDTO,
  ILead,
  ILeadAPIResponse,
  ILeadSearchFilter,
  LogAction,
  LogContextType,
  LogEntityType,
  assignClientContact,
  assignClientUser,
  getAllLeadStatusByVisibility,
} from 'wisestep-api';
import { WsColors } from 'wisestep-base';
import { object, string } from 'yup';

import {
  defaultClientContactDTO,
  defaultClientDTO,
} from '../../clientListingPage/helper/ClientListingHelper';

export const defaultLeadSearchFilter: ILeadSearchFilter = {
  source: [],
  status: [],
  leadOwners: [],
  businessUnitIds: [],
  orgUnitIds: [],
  tags: [],
  clientIds: [],
  contactIds: [],
  query: '',
  workspaceId: '',
  page: 0,
  size: 25,
  sortOrder: 'asc',
  sortBy: 'createdOn',
  fromDate: '',
  toDate: '',
  createdByIds: [],
  filterByDateType: '',
  clientOperator: 'is',
  contactOperator: 'is',
  createdByOperator: 'is',
  sourceOperator: 'is',
  tagsOperator: 'is',
  leadOwnersOperator: 'is',
};

export const defaultLeadsData: ILeadAPIResponse = {
  content: [],
  empty: true,
  first: true,
  last: true,
  number: 0,
  numberOfElements: 0,
  size: 0,
  totalElements: 0,
  totalPages: 0,
};

export const mapToILeadAdvanceFilter = (data): ILeadSearchFilter => {
  return {
    leadOwners: data.leadOwners ?? [],
    businessUnitIds:
      typeof data.businessUnitIds === 'string' ? [data.businessUnitIds.split(' & ')[0]] : [],
    orgUnitIds: data?.orgUnitIds?.map((o) => o.id) ?? [],
    tags: data.tags ?? [],
    clientIds:
      data.clientIds?.map((client: ClientDTO) => ({ id: client.id, name: client.name })) ?? [],
    contactIds:
      data.contactIds?.map((contact: ClientContactDTO) => ({
        id: contact.id,
        firstName: contact.firstName,
        emailAddress: contact.emailAddress,
      })) ?? [],
    source: data.source ?? [],
    status: data.status ?? [],
    query: data.query ?? '',
    workspaceId: '',
    page: 0,
    size: 25,
    sortOrder: 'asc',
    sortBy: 'createdOn',
    filterByDateType:
      data.filterByDateType === 'UPDATED_BETWEEN' ? 'UPDATED_BETWEEN' : 'CREATED_BETWEEN',
    fromDate: data.fromDate,
    toDate: data.toDate,
    createdByIds: data.createdByIds,
    clientOperator: data.clientOperator ?? 'is',
    contactOperator: data.contactOperator ?? 'is',
    createdByOperator: data.createdByOperator ?? 'is',
    sourceOperator: data.sourceOperator ?? 'is',
    tagsOperator: data.tagsOperator ?? 'is',
    leadOwnersOperator: data.leadOwnersOperator ?? 'is',
  };
};

export const tabsList = [{ tabName: 'Activty' }, { tabName: 'More Info' }];

export const defaultLeadActivityLogDTO: ActivityLogDTO = {
  id: '',
  field: '',
  entityId: '',
  parentEntitiyId: '',
  entityName: '',
  description: '',
  parentEntitiyName: '',
  action: LogAction.Add,
  entityType: LogEntityType.Client,
  parentEntitiyType: LogContextType.Client,
  createdOn: new Date(),
  createdByName: '',
  workspaceId: '',
  createdBy: '',
  contextData: '',
};

export const defaultLeadDTO: ILead = {
  id: '',
  leadName: '',
  client: defaultClientDTO,
  clientContact: defaultClientContactDTO('', ''),
  source: '',
  status: '',
  leadOwnerInfo: {
    id: null,
    name: null,
  },
  businessUnitsInfo: {
    buinfo: [],
  },
  organisationEntityId: '',
  organisationEntityName: '',
  tags: [],
  linkedinUrl: '',
  description: '',
  workspaceId: '',
};

export const getLeadCommonGridStyles = (): SxProps<Theme> => ({
  [`& .${gridClasses.cell}`]: {
    py: 1,
  },
  '& .MuiDataGrid-root': {
    borderRadius: 0,
  },
  '& .MuiDataGrid-row:hover': {
    backgroundColor: 'transparent',
  },
  '& .MuiDataGrid-cell:focus': {
    outline: 'none',
  },
  borderTopRightRadius: '0',
  borderTopLeftRadius: '0',
  borderTop: '0',
});

export const StyledEventNoteIcon = styled(EventNoteIcon)(() => ({
  height: 23,
  width: 23,
  color: WsColors.TheBlues500,
}));

export const statusOptions = [
  { label: 'Open', value: 'Open' },
  { label: 'Closed', value: 'Closed' },
];

export interface ITableLead {
  id: string;
  leadName: string;
  clientName?: string;
  contactName: string;
  businessUnitsNames: string;
  orgName?: string;
  leadOwnerName: string;
  createdOn: string;
  createdByName?: string;
  status: string;
  source: string;
  description: string;
  linkedinUrl: string;
  tags: string[];
  rowIndex: number;
}

export const enum LeadUpdateDialog {
  STATUS = 'STATUS',
  OWNER = 'OWNER',
}

export const leadFilterDateOptions = [
  {
    label: 'Created Between',
    value: 'CREATED_BETWEEN',
  },
  {
    label: 'Updated Between',
    value: 'UPDATED_BETWEEN',
  },
];

export const shareClientAndContact = async (lead, recruiter) => {
  const clientShareDTO = {
    clientIds: lead?.client?.id ? [lead.client.id] : [],
    userList: recruiter?.email,
  };
  await assignClientUser(clientShareDTO);

  const contactShareDTO: ClientContactShareMultipleDTO = {
    clientId: lead?.client?.id as string,
    clientContactIds: lead?.clientContact?.id ? [lead.clientContact.id] : [],
    userList: recruiter?.email,
  };
  await assignClientContact(contactShareDTO);
};

export const limitedPrivacyOptions = new Map<string, string>();
limitedPrivacyOptions.set('Right to Restrict', 'restrict');

export const getValidationSchema = (workspaceId: string) => {
  const isOptionalWorkspace = workspaceId === 'c0de09a6-0678-4e65-aa30-25345c74acb6';

  return object().shape({
    leadName: string().required('Please provide LeadName'),
    leadStatusId: string().required('Please provide Status'),
    leadOwnerId: string().required('Please provide Lead Owner'),
    companyName: string().required('Please provide Client'),
    clientRecruiter: string().required('Please provide Client Contact'),
    description: string().required('Please provide Description'),
    linkedinUrl: isOptionalWorkspace
      ? string().notRequired()
      : string().required('Please provide Job URL'),
  });
};

export const getLeadStatusByName = async (statusName: string) => {
  const leadStatus = await getAllLeadStatusByVisibility();
  return leadStatus.data.find((status) => status.leadStatusName === statusName);
};

export const operatorOptions = [
  { label: 'is', value: 'is' },
  { label: 'is not', value: 'is_not' },
  { label: 'is blank', value: 'is_blank' },
  { label: 'is not blank', value: 'is_not_blank' },
];
